import * as Sentry from "@sentry/vue";
import type { UserDto } from "@umi/api/schemas/user";
import { sameAddress } from "@umi/shared/address";
import { getAuthSigMessage, getEncryptionSigMessage } from "@umi/shared/auth";
import { catchBlock } from "@umi/shared/catchBlock";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { createWalletClient, custom, toHex, UserRejectedRequestError, type Address, type Chain, type WalletClient } from "viem";
import { mainnet } from "viem/chains";



interface State {
  user: UserDto | null,
  encryptionKey: string | null,
  isLoadingJwt: boolean,
  isUnlockingVault: boolean,
  fetchingData: boolean,
  loading: boolean,
  websocket: WebSocket | null,
  mismatchedWalletModal: boolean,
  activeBrowserAddress: Address | null,
  walletClient: WalletClient | null,
}


export const useAuthStore = defineStore("auth", {

  state: (): State => ({
    user: null,
    encryptionKey: null,
    isLoadingJwt: false,
    isUnlockingVault: false,
    fetchingData: false,
    loading: false,
    websocket: null,
    mismatchedWalletModal: false,
    activeBrowserAddress: null,
    walletClient: null,
  }),


  getters: {

    isUser: (state) => {
      return {
        cursed: !!(state.user?.flags.isCursed),
      };
    },

    browserWalletInstance() {
      switch (LocalStorage.browserWallet.value) {
        case "PHANTOM": return window.phantom.ethereum;
        case "METAMASK": return window.ethereum;
        default: throw new Error("Invalid browser wallet instance.");
      }
    },

    discordLinkRequired(): boolean {
      const user = this.user;
      if (!user) return false;
      return !user.flags.isPresent || !user.flags.isVerified;
    },

    jwt: () => LocalStorage.jwt.value,

    // Returns the account address or the currently authenticated user.
    accountAddress(state): Address | null {
      if (!LocalStorage.jwt.value || !this.isAuthenticated) return null;
      const decodedJwt = jwtDecode(LocalStorage.jwt.value) as { accountAddress: Address } | null;
      if (decodedJwt) return decodedJwt.accountAddress;
      if (!state.user) return null;
      return state.user.accountAddress;
    },

    /**
     * Whether we have a usable (ie not expired) JWT available.
     * @param state
     */
    isAuthenticated(): boolean {
      const [isValid, err] = catchBlock(() => {
        if (!LocalStorage.jwt.value) return false;
        const decoded = jwtDecode(LocalStorage.jwt.value);
        if (!decoded.exp) return false;
        const bufferTimeInMinutes = 5;
        return decoded.exp * 1000 > Date.now() + bufferTimeInMinutes * 60 * 1000;
      });
      return err ? false : isValid!;
    },

    isLoaded(): boolean { return !!this.user && this.isAuthenticated; },

    connectedAddress(): Address | null {
      if (this.accountAddress) return this.accountAddress;
      return this.activeBrowserAddress;
    },

  },


  actions: {

    async getBrowserClient(chain: Chain = mainnet) {
      await this.browserWalletInstance.request?.({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: toHex(chain.id) }]
      });
      await this.browserWalletInstance.enable();
      const transport = custom(this.browserWalletInstance);
      return createWalletClient({ chain, transport });
    },


    async toggleMismatchedWalletModal(show: boolean) {
      if (show) {
        await this.browserWalletInstance.enable();
        const transport     = custom(this.browserWalletInstance);
        const walletClient  = createWalletClient({ chain: mainnet, transport });
        const [account]     = await walletClient.getAddresses();
        this.activeBrowserAddress = account ?? null;
      } else {
        this.activeBrowserAddress = null;
      }
      this.mismatchedWalletModal = show;
    },

    async loadUser() {
      if (this.user) return;
      this.loading = true;
      const [error, user, , isSuccess] = await useSafeORPC().user.getUser();
      this.loading = false;
      if (error) {
        console.error(error);
        if (isUnauthorizedError(error)) {
          if (this.jwt) this.logout();
          return;
        }
        Sentry.captureException(error);
        return;
      }
      if (!isSuccess || !user) return;
      this.user = user;
    },

    async unlock() {

      const browserWallet = this.browserWalletInstance;

      try {

        await browserWallet.enable();
        const transport     = custom(browserWallet);
        const walletClient  = createWalletClient({ chain: mainnet, transport });
        const [account]     = await walletClient.getAddresses();
        const userAccount   = this.user?.accountAddress;

        if (!account) return false;

        if (!userAccount || !sameAddress(userAccount, account)) {
          this.mismatchedWalletModal = true;
          this.encryptionKey = null;
          return false;
        }

        if (this.encryptionKey) return true;

        const encryptionKey = await walletClient.signMessage({ account, message: getEncryptionSigMessage("v1") });
        this.encryptionKey  = encryptionKey;
        return true;

      } catch (err) {
        if (err instanceof UserRejectedRequestError) {
          showInfoNotification("You need to generate your encryption key to continue.");
          return false;
        }
        throw err;
      }

    },

    async authenticate(browserWallet: "PHANTOM" | "METAMASK") {

      const [,err] = await catchBlock(async () => {

        /**
         * @important
         * If we already have a valid JWT stored, we don't
         * need to authenticate again.
         */
        if (this.isAuthenticated) return;


        this.isLoadingJwt = true;

        LocalStorage.browserWallet.value = browserWallet;
        LocalStorage.jwt.value = null;

        await this.browserWalletInstance.enable();
        const transport     = custom(this.browserWalletInstance);
        const walletClient  = createWalletClient({ chain: mainnet, transport });
        const [account]     = await walletClient.getAddresses();
        if (!account) throw new Error("No account found");

        const orpc = useSafeORPC();
        const [nonceError, nonce, , nonceOk] = await orpc.user.getNonce({ address: account });
        if (nonceError || !nonceOk || !nonce) throw nonceError ?? new Error("Failed to get nonce");

        const authSignature = await walletClient.signMessage({ account, message: getAuthSigMessage(nonce) });

        const [authError, jwt, , authOk] = await orpc.user.authenticate({ signerAddress: account, signature: authSignature });
        if (authError || !authOk || !jwt) throw authError ?? new Error("Failed to authenticate");

        LocalStorage.jwt.value = jwt;

        this.isLoadingJwt = false;

        return navigateTo("/mints");
      });


      if (err) {
        this.isLoadingJwt = false;

        const isUserRejection = err instanceof UserRejectedRequestError
          || (typeof err === "object" && err !== null && "code" in err && (err as any).code === 4001);

        if (isUserRejection) {
          showInfoNotification("You need to authenticate to continue.");
        } else {
          console.error(err);
          const error = err instanceof Error ? err : new Error(typeof err === "object" && err !== null && "message" in err ? String((err as any).message) : String(err));
          Sentry.captureException(error, { extra: { originalError: err } });
          showErrorNotification("An error occurred while authenticating. Please try again.");
        }
      }

      connectWebsocket();

    },

    async connectWallet(browser: "METAMASK" | "PHANTOM", chain: Chain) {
      LocalStorage.browserWallet.value = browser;
      const browserWallet = this.browserWalletInstance;

      await browserWallet.request({ method: "eth_requestAccounts" });

      try {
        await browserWallet.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${chain.id.toString(16)}` }],
        });
      } catch (switchError: any) {
        if (switchError.code === 4902) {
          await browserWallet.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: `0x${chain.id.toString(16)}`,
              chainName: chain.name,
              nativeCurrency: chain.nativeCurrency,
              rpcUrls: chain.rpcUrls.default.http,
              blockExplorerUrls: [chain.blockExplorers?.default.url],
            }],
          });
        }
      }

      this.walletClient = createWalletClient({ chain, transport: custom(browserWallet) });
      const [address] = await this.walletClient.getAddresses();
      this.activeBrowserAddress = address ?? null;
    },

    logout() {
      disconnectWebsocket();
      LocalStorage.jwt.value = null;
      LocalStorage.browserWallet.value = null;
      this.encryptionKey = null;
      this.user = null;
      this.walletClient = null;
      this.activeBrowserAddress = null;
      const route = useRoute();
      if (["/plans", "/pricing", "/subscription"].includes(route.path)) return;
      navigateTo("/");
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
